import { Controller } from "stimulus";

const PER_PAGE = 25;

export default class extends Controller {
  static targets = ["tableBody", "loadingIndicator", "scrollTrigger"];

  connect() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMore = false;

    if (this.hasScrollTriggerTarget) {
      const trigger = this.scrollTriggerTarget;
      this.currentPage = parseInt(trigger.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(trigger.dataset.totalPages, 10) || 1;
      this.perPage = parseInt(trigger.dataset.perPage, 10) || PER_PAGE;
      this.hasMore = this.currentPage < this.totalPages;

      if (this.hasMore) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && this.hasMore && !this.isLoading) {
                this.loadNextPage();
              }
            });
          },
          {
            rootMargin: "200px",
            threshold: 0.1,
          }
        );

        this.observer.observe(trigger);
      }
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta && meta.content;
  }

  showToast(message) {
    window.dispatchEvent(
      new CustomEvent("toast:show", {
        detail: { message },
      })
    );
  }

  async enable(event) {
    const button = event.currentTarget;
    if (button.disabled) return;

    const pupilId = button.dataset.pupilId;
    if (!pupilId) return;

    button.disabled = true;

    try {
      const response = await fetch("/schools/reports/disabled-scheduling/enable", {
        method: "POST",
        headers: {
          Accept: "text/html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfToken,
        },
        body: new URLSearchParams({ pupil_id: pupilId }),
      });

      if (response.status === 204) {
        const row = document.getElementById(`disabled_scheduling_pupil_${pupilId}`);
        if (row) {
          row.remove();
        }

        if (this.hasTableBodyTarget && this.tableBodyTarget.querySelectorAll("tr[id^='disabled_scheduling_pupil_']").length === 0) {
          this.tableBodyTarget.innerHTML = `
            <tr>
              <td colspan="5" class="u-textCenter  u-textGreyDark  p2">
                No students with BTW scheduling disabled.
              </td>
            </tr>
          `;
        }
        this.showToast("BTW scheduling enabled");
        return;
      }

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Failed to enable scheduling: ${response.status}`);
      }
    } catch (error) {
      console.error("Error enabling scheduling:", error);
      button.disabled = false;
      this.showToast(error.message || "Could not enable scheduling");
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;
    this.currentPage += 1;

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = "block";
    }

    try {
      const url = `/schools/reports/disabled-scheduling/page?page=${this.currentPage}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/html",
          "X-CSRF-Token": this.csrfToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load page: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      const tempTable = document.createElement("table");
      const tempTableBody = document.createElement("tbody");
      tempTableBody.innerHTML = html.trim();
      tempTable.appendChild(tempTableBody);

      const rows = Array.from(tempTableBody.querySelectorAll("tr"));

      if (rows.length === 0) {
        this.hasMore = false;
        if (this.hasScrollTriggerTarget) {
          this.scrollTriggerTarget.style.display = "none";
        }
      } else {
        if (this.hasTableBodyTarget) {
          const emptyRow = this.tableBodyTarget.querySelector("td[colspan]");
          if (emptyRow) {
            emptyRow.closest("tr")?.remove();
          }
          rows.forEach((row) => {
            this.tableBodyTarget.appendChild(row);
          });
        }

        if (rows.length < this.perPage) {
          this.hasMore = false;
          if (this.hasScrollTriggerTarget) {
            this.scrollTriggerTarget.style.display = "none";
          }
        } else if (this.hasScrollTriggerTarget) {
          this.scrollTriggerTarget.dataset.currentPage = String(this.currentPage);
        }
      }
    } catch (error) {
      console.error("Error loading next page:", error);
      this.currentPage -= 1;
      this.showToast("Could not load more students");
    } finally {
      this.isLoading = false;
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.style.display = "none";
      }
    }
  }
}
